import { useTranslation } from "react-i18next"

import type { GraphFile, GraphPerson, UserProfile } from "@/api/home-context"

import { Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { usePrismMemory } from "@/hooks/use-prism-memory"

import { OnboardingCheckOption, OnboardingProfileRow } from "./onboarding-page-components"

export function OnboardingPersonalizationStep({
  aboutMe,
  busy,
  completionPending,
  files,
  homeContextError,
  maxAboutMeLength,
  people,
  profile,
  rememberFiles,
  rememberPeople,
  onAboutMeChange,
  onContinue,
  onRememberFilesChange,
  onRememberPeopleChange,
  onSkip,
}: {
  aboutMe: string
  busy: boolean
  completionPending: boolean
  files: GraphFile[]
  homeContextError: boolean
  maxAboutMeLength: number
  people: GraphPerson[]
  profile: UserProfile | undefined
  rememberFiles: boolean
  rememberPeople: boolean
  onAboutMeChange: (value: string) => void
  onContinue: () => void
  onRememberFilesChange: (checked: boolean) => void
  onRememberPeopleChange: (checked: boolean) => void
  onSkip: () => void
}) {
  const { t } = useTranslation()
  const prismMemory = usePrismMemory()

  return (
    <div>
      <div className="text-center">
        <h1
          data-onboarding-step-heading
          className="font-display text-2xl font-semibold sm:text-3xl"
          tabIndex={-1}
        >
          {t("personalization.smoothOnboarding.personalization.title")}
        </h1>
        <p className="text-muted-foreground mx-auto mt-2 max-w-2xl">
          {t("personalization.smoothOnboarding.personalization.description")}
        </p>
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("personalization.onboarding.role.title")}</CardTitle>
            <CardDescription>{t("personalization.onboarding.role.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <OnboardingProfileRow
              label={t("personalization.onboarding.role.name")}
              value={profile?.name}
            />
            <OnboardingProfileRow
              label={t("personalization.onboarding.role.jobTitle")}
              value={profile?.job_title}
            />
            <OnboardingProfileRow
              label={t("personalization.onboarding.role.department")}
              value={profile?.department}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("personalization.onboarding.people.title")}</CardTitle>
            <CardDescription>{t("personalization.onboarding.people.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {people.length ? (
              <ul className="space-y-1">
                {people.map((person) => person.name && <li key={person.id}>{person.name}</li>)}
              </ul>
            ) : (
              <p className="text-muted-foreground">
                {t("personalization.onboarding.people.empty")}
              </p>
            )}
            {!prismMemory && people.length > 0 && (
              <OnboardingCheckOption
                checked={rememberPeople}
                id="remember-people"
                label={t("personalization.onboarding.people.remember")}
                onChange={onRememberPeopleChange}
              />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("personalization.onboarding.files.title")}</CardTitle>
            <CardDescription>{t("personalization.onboarding.files.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {files.length ? (
              <ul className="space-y-1">
                {files.map(
                  (file) =>
                    file.name && (
                      <li key={file.id} className="truncate">
                        {file.name}
                      </li>
                    )
                )}
              </ul>
            ) : (
              <p className="text-muted-foreground">{t("personalization.onboarding.files.empty")}</p>
            )}
            {!prismMemory && files.length > 0 && (
              <OnboardingCheckOption
                checked={rememberFiles}
                id="remember-files"
                label={t("personalization.onboarding.files.remember")}
                onChange={onRememberFilesChange}
              />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("personalization.onboarding.aboutMe.title")}</CardTitle>
            <CardDescription>{t("personalization.onboarding.aboutMe.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              className="min-h-28"
              data-testid="onboarding-about-me"
              maxLength={maxAboutMeLength}
              placeholder={t("personalization.onboarding.aboutMe.placeholder")}
              value={aboutMe}
              onChange={(event) => onAboutMeChange(event.target.value)}
            />
            <p className="text-muted-foreground mt-2 text-right text-xs">
              {aboutMe.length}/{maxAboutMeLength}
            </p>
          </CardContent>
        </Card>
      </div>
      {homeContextError && (
        <p className="text-muted-foreground mt-4 text-center text-sm">
          {t("personalization.smoothOnboarding.personalization.unavailable")}
        </p>
      )}
      <div className="mt-6 flex flex-col-reverse justify-end gap-3 sm:flex-row">
        <Button className="min-h-10" disabled={busy} variant="outline" onClick={onSkip}>
          {t("personalization.smoothOnboarding.personalization.skip")}
        </Button>
        <Button
          className="min-h-10"
          data-testid="onboarding-confirm"
          disabled={busy}
          onClick={onContinue}
        >
          {completionPending && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
          {t("personalization.smoothOnboarding.personalization.continue")}
        </Button>
      </div>
    </div>
  )
}
